import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTrustHubProfile, fetchTrustHubDashboard } from '../../../../store/trustHubSlice';
import ProfileStatusCard from '../components/profile/ProfileStatusCard';
import ProfileWizard from '../components/profile/ProfileWizard';
import EmptyState from '../components/shared/EmptyState';
import LoadingSkeleton from '../components/shared/LoadingSkeleton';
import './BusinessProfileTab.css';

const BusinessProfileTab = ({ user }) => {
  const dispatch = useDispatch();
  const { profile, profileLoading, profileError } = useSelector((state) => state.trustHub);

  const [isEditing, setIsEditing] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const userId = user?.user_id || user?.id;

  useEffect(() => {
    if (userId) {
      console.log('BusinessProfileTab - Fetching profile for user:', userId);
      dispatch(fetchTrustHubProfile(userId));
    }
  }, [dispatch, userId]);

  // Track when initial loading is complete
  useEffect(() => {
    if (!profileLoading && initialLoad) {
      // Add a small delay to prevent flash of content
      const timer = setTimeout(() => {
        setInitialLoad(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [profileLoading, initialLoad]);

  // Debug: Log profile status
  useEffect(() => {
    if (profile) {
      console.log('Profile status:', profile.status);
      console.log('Should show edit button:', profile.status !== 'twilio-approved');
    }
  }, [profile]);

  // Handle loading state - show during initial load or when refetching
  if (profileLoading || initialLoad) {
    return (
      <div className="business-profile-tab">
        <div style={{ position: 'relative', minHeight: '400px' }}>
          <LoadingSkeleton variant="dashboard" />
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(3px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}>
            <div style={{ textAlign: 'center' }}>
              <div className="ui active inline loader" style={{ marginBottom: '15px' }}></div>
              <p style={{ color: '#6c757d', fontSize: '14px', fontWeight: 500 }}>
                Loading business profile...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Handle error state
  if (profileError) {
    return (
      <div className="business-profile-tab">
        <div className="alert alert-danger">
          <h4>Error Loading Profile</h4>
          <p>{profileError}</p>
          <button
            className="btn linear-gradient zy-save-btn ml-2 mt-3"
            onClick={() => userId && dispatch(fetchTrustHubProfile(userId))}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  // No profile exists - show empty state
  if (!profile || !profile.id) {
    if (isEditing) {
      return (
        <>
          <div className="business-profile-tab">
            <EmptyState
              title="No profile exists"
              message="Create your Business profile to start using compliance features."
            />
          </div>
          <div className="wizard-modal-overlay">
            <ProfileWizard
              userId={userId}
              onCancel={() => setIsEditing(false)}
              onComplete={() => {
                setIsEditing(false);
                dispatch(fetchTrustHubProfile(userId));
                dispatch(fetchTrustHubDashboard(userId));
              }}
            />
          </div>
        </>
      );
    }

    return (
      <div className="business-profile-tab">
        <EmptyState
          title="No profile exists"
          message="Create your Business profile to start using compliance features for Voice, SMS, and VoIP communications."
          actionLabel="Create Business Profile"
          onAction={() => setIsEditing(true)}
        >
          <p className="empty-state-description">
            A Business profile is required by to verify your business identity
            and comply with regulatory requirements.
          </p>
          {/* <a
            href="https://www.twilio.com/docs/trust-hub"
            target="_blank"
            rel="noopener noreferrer"
            className="learn-more-link"
          >
            📄 Learn more about Business requirements
          </a> */}
        </EmptyState>
      </div>
    );
  }

  // Editing mode - show ProfileWizard
  if (isEditing) {
    return (
      <>
        <div className="business-profile-tab">
          <div className="profile-header-section">
            <ProfileStatusCard
              profile={profile}
            />
          </div>
        </div>
        <div className="wizard-modal-overlay">
          <ProfileWizard
            userId={userId}
            existingProfile={profile}
            onCancel={() => setIsEditing(false)}
            onComplete={() => {
              setIsEditing(false);
              dispatch(fetchTrustHubProfile(userId));
              dispatch(fetchTrustHubDashboard(userId));
            }}
          />
        </div>
      </>
    );
  }

  // View mode - display profile details
  return (
    <>
      <div className="business-profile-tab">
      <div className="profile-header-section">
        <ProfileStatusCard
          profile={profile}
        />
        {/* Show Edit button - Hide only for pending/in-review/approved statuses */}
        {/* User CAN edit for: null (initial), draft, rejected statuses */}
        {/* User CANNOT edit for: pending-review, in-review, twilio-approved, pending_trusthub */}
        {!['pending-review', 'in-review', 'twilio-approved', 'pending_trusthub'].includes(profile.status) && (
          <button
            className="btn linear-gradient zy-save-btn"
            onClick={() => setIsEditing(true)}
          >
            Edit Profile
          </button>
        )}
      </div>

      {/* General Information Section */}
      <div className="profile-section">
        <h3 className="section-title">General Information</h3>
        <div className="profile-details-grid">
          <div className="detail-item">
            <label>Business Name:</label>
            <span>{profile.profile_friendly_name || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Legal Business Name:</label>
            <span>{profile.legal_business_name || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Country:</label>
            <span>{profile.country?.toUpperCase() || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Business Type:</label>
            <span>{profile.business_type || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Business Industry:</label>
            <span>{profile.business_industry?.replace(/_/g, ' ') || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Tax ID ({profile.business_registration_id_type || 'EIN'}):</label>
            <span>{profile.business_registration_number || 'N/A'}</span>
          </div>
        </div>
      </div>

      {/* Business Contact Information */}
      <div className="profile-section">
        <h3 className="section-title">Business Contact Information</h3>
        <div className="profile-details-grid">
          <div className="detail-item full-width">
            <label>Business Address:</label>
            <span>
              {profile.address_street1}
              {profile.address_street2 && `, ${profile.address_street2}`}
              <br />
              {profile.address_city}, {profile.state?.abbreviation || profile.address_state} {profile.address_postal_code}
            </span>
          </div>
          <div className="detail-item">
            <label>Business Email:</label>
            <span>{profile.notification_email || 'N/A'}</span>
          </div>
          <div className="detail-item">
            <label>Website:</label>
            <span>
              {profile.business_website ? (
                <a href={profile.business_website} target="_blank" rel="noopener noreferrer">
                  {profile.business_website}
                </a>
              ) : 'N/A'}
            </span>
          </div>
        </div>
      </div>

    </div>
    </>
  );
};

export default BusinessProfileTab;
